/**
 * useShapeMetrics — computes the size + population readout for a map's radius and
 * drawn (polygon/rectangle) groups, for the group-card summary line and the
 * on-canvas shape labels (approved in mockups/03-editor-shape-labels.html).
 *
 * Only radius/drawn groups get a readout — a census-backed group (ZIPs, cities,
 * counties, states, tracts) does not (per the mock-up: "Franchise Zone A" full of
 * ZIPs shows no summary). A group qualifies when it has exactly one area and that
 * area's type is 'radius' or 'drawn' — which is exactly what the canvas draw tools
 * and the group-card radius entry produce (one shape → one group → one area).
 *
 * Size is computed locally (no round-trip): radius is params.radius_mi; drawn is
 * polygonAreaSqMi(geometry). Population comes from POST /api/geo/query with
 * kind:'zips' and the group's shape (center+radius_mi, or drawn_geometry) — the
 * SAME membership rule (intersects, uniformly — decision 3) the rest of the app
 * uses, so the population here matches what an export of that shape would total.
 * We sum ZIP POP100 because ZCTAs tile the country without overlap, giving a
 * clean population sum for an arbitrary shape.
 *
 * WHY it re-queries on load: population is deliberately NOT persisted (no schema
 * column — decision recorded in project-state). So this hook recomputes it every
 * time the Editor mounts a map. To avoid re-firing on every unrelated autosave
 * (which re-mints group ids server-side), results are cached by a SHAPE SIGNATURE
 * derived from the geometry/params content, not the group id. Editing group B
 * won't refetch group A's population; only a changed shape (or a fresh mount with
 * an empty cache) fires a query. The cache lives for the hook's lifetime (the
 * Editor session); a full reload starts cold and refetches — an accepted trade-off
 * for not persisting population (see the developer's report / project-state).
 */

import { useEffect, useRef, useState } from 'react';
import api from '../utils/api';
import { polygonAreaSqMi, polygonCentroid, formatSqMi, formatRadiusMi } from '../utils/geometry';

// Cap how many characters of a drawn geometry feed the signature. A drawn shape's
// full coordinate list is stable per-shape; hashing the whole thing is wasteful,
// but we want enough to distinguish two different shapes. We fold the whole
// coordinate string into a short numeric hash instead of slicing (slicing could
// collide two shapes that share a prefix).

/**
 * Determine whether a group qualifies for a size/population readout, and return
 * its single shape area if so. Returns null for census-backed or multi-area or
 * empty groups.
 *
 * @param {object} group
 * @returns {{ kind: 'radius'|'drawn', area: object }|null}
 */
function shapeAreaOf(group) {
  const areas = group?.areas || [];
  if (areas.length !== 1) return null;
  const area = areas[0];
  if (area.area_type === 'radius' && area.params?.center && area.params?.radius_mi) {
    return { kind: 'radius', area };
  }
  if (area.area_type === 'drawn' && area.geometry && area.geometry.type) {
    return { kind: 'drawn', area };
  }
  return null;
}

/**
 * Stable content signature for a shape, so the population cache survives the
 * group-id churn of autosave. Radius: center + radius. Drawn: a cheap rolling
 * hash of the coordinate string. Different shapes → different signatures;
 * the same shape across saves → the same signature (cache hit, no refetch).
 *
 * @param {{ kind: string, area: object }} shape
 * @returns {string}
 */
function signatureOf({ kind, area }) {
  if (kind === 'radius') {
    const [lat, lon] = area.params.center;
    return `r:${lat.toFixed(5)},${lon.toFixed(5)}:${area.params.radius_mi}`;
  }
  // Drawn: fold the coordinate JSON into a 32-bit rolling hash (djb2-ish).
  const str = JSON.stringify(area.geometry.coordinates || []);
  let h = 5381;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  }
  return `d:${area.geometry.type}:${str.length}:${h >>> 0}`;
}

/**
 * Build the request body for POST /api/geo/query for a given shape. Always
 * kind:'zips' (ZCTAs tile without overlap → clean population sum).
 *
 * @param {{ kind: string, area: object }} shape
 * @returns {object} request body
 */
function queryBodyFor({ kind, area }) {
  if (kind === 'radius') {
    return { kind: 'zips', center: area.params.center, radius_mi: area.params.radius_mi };
  }
  return { kind: 'zips', drawn_geometry: area.geometry };
}

/**
 * Sum ZIP populations from a /api/geo/query response. POP100 can be null for a
 * ZCTA (unknown population — kept distinct from 0 server-side); nulls contribute
 * 0 to the sum. Returns { population, hasUnknown } so the UI could note partial
 * coverage later; today it just shows the sum.
 *
 * @param {object} result - { feats: [...], truncated }
 * @returns {{ population: number, truncated: boolean }}
 */
function summarizeResult(result) {
  const feats = Array.isArray(result?.feats) ? result.feats : [];
  let population = 0;
  for (const f of feats) {
    const pop = f?.properties?.POP100;
    if (typeof pop === 'number' && Number.isFinite(pop)) population += pop;
  }
  return { population, truncated: result?.truncated === true };
}

/**
 * useShapeMetrics(groups) → a map keyed by group id:
 *   {
 *     [groupId]: {
 *       kind: 'radius'|'drawn',
 *       sizeLabel: '10 mi radius' | '12.4 sq mi',
 *       centroid: [lat, lon],            // on-canvas label anchor
 *       loading: boolean,                // population query in flight
 *       error: boolean,                  // population query failed
 *       population: number|null,         // null until loaded / on error
 *       truncated: boolean,              // the shape's ZIP set hit a source cap
 *     }
 *   }
 * Groups that don't qualify are simply absent from the map.
 *
 * @param {Array<object>} groups - the map document's groups
 * @returns {Record<string, object>}
 */
export default function useShapeMetrics(groups) {
  const [metrics, setMetrics] = useState({});

  // Population cache keyed by shape signature (survives group-id churn). Value:
  // { status: 'loading'|'done'|'error', population, truncated }.
  const popCacheRef = useRef(new Map());
  // Track in-flight signatures so two groups with the same shape don't double-fetch.
  const inFlightRef = useRef(new Set());
  // Guard state updates after unmount.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const list = groups || [];

    // 1) Compute the synchronous part (kind, size, centroid) for every qualifying
    //    group, and figure out which shape signatures still need a population fetch.
    const next = {};
    const toFetch = []; // { signature, body }

    for (const group of list) {
      const shape = shapeAreaOf(group);
      if (!shape) continue;

      const sig = signatureOf(shape);
      const sizeLabel =
        shape.kind === 'radius'
          ? formatRadiusMi(shape.area.params.radius_mi)
          : formatSqMi(polygonAreaSqMi(shape.area.geometry));
      const centroid =
        shape.kind === 'radius'
          ? shape.area.params.center
          : polygonCentroid(shape.area.geometry);

      const cached = popCacheRef.current.get(sig);
      const entry = {
        kind: shape.kind,
        sizeLabel,
        centroid,
        loading: !cached || cached.status === 'loading',
        error: cached ? cached.status === 'error' : false,
        population: cached && cached.status === 'done' ? cached.population : null,
        truncated: cached && cached.status === 'done' ? cached.truncated : false,
      };
      next[group.id] = entry;

      // Queue a fetch if this shape has never been queried (and isn't already
      // being fetched by an identical shape this pass).
      if (!cached && !inFlightRef.current.has(sig)) {
        toFetch.push({ signature: sig, body: queryBodyFor(shape) });
      }
    }

    setMetrics(next);

    if (toFetch.length === 0) return;

    // 2) Fire the population queries. Mark each signature loading up front so a
    //    re-run of this effect (e.g. an unrelated edit) doesn't re-queue it.
    let cancelled = false;
    toFetch.forEach(({ signature, body }) => {
      popCacheRef.current.set(signature, { status: 'loading' });
      inFlightRef.current.add(signature);

      api
        .post('/geo/query', body)
        .then((result) => {
          const { population, truncated } = summarizeResult(result);
          popCacheRef.current.set(signature, { status: 'done', population, truncated });
        })
        .catch(() => {
          // A failed population lookup shouldn't wedge the UI — mark it errored so
          // the card can show a dash instead of a perpetual spinner. Not cached as
          // 'done', so a later edit that re-runs the effect will retry it.
          popCacheRef.current.set(signature, { status: 'error' });
        })
        .finally(() => {
          inFlightRef.current.delete(signature);
          if (cancelled || !mountedRef.current) return;
          // Re-derive metrics from the current groups + freshly-updated cache.
          // Reading groups here is safe: this closure captured the same `groups`
          // the effect ran for; a newer groups value would have re-run the effect.
          setMetrics((prev) => applyCacheToMetrics(prev, groups, popCacheRef.current));
        });
    });

    return () => {
      cancelled = true;
    };
    // Re-run whenever the groups reference changes (Editor updates it immutably).
  }, [groups]);

  return metrics;
}

/**
 * Recompute the loading/population/error fields on an existing metrics map from
 * the population cache, preserving the already-computed size/centroid fields.
 * Used after a fetch settles so only the changed group flips out of "loading".
 *
 * @param {Record<string, object>} prev - current metrics map
 * @param {Array<object>} groups - the same groups the effect ran for
 * @param {Map<string, object>} cache - signature → { status, population, truncated }
 * @returns {Record<string, object>}
 */
function applyCacheToMetrics(prev, groups, cache) {
  const out = { ...prev };
  for (const group of groups || []) {
    const existing = out[group.id];
    if (!existing) continue;
    const shape = shapeAreaOf(group);
    if (!shape) continue;
    const cached = cache.get(signatureOf(shape));
    if (!cached) continue;
    out[group.id] = {
      ...existing,
      loading: cached.status === 'loading',
      error: cached.status === 'error',
      population: cached.status === 'done' ? cached.population : null,
      truncated: cached.status === 'done' ? cached.truncated : false,
    };
  }
  return out;
}
